import { Button, Group, Popover, Text, Textarea, UnstyledButton } from '@mantine/core';
import { IconNote } from '@tabler/icons-react';
import { MouseEvent, ReactElement, useState } from 'react';
import { alert } from '@/services/alert';
import { showUndoToast } from '@/services/undoToast';
import { ORBIT_DISABLED } from '@/theme/colors';

/**
 * A row's note as an icon: grey when empty, dark with a note, the note on
 * hover. A click edits it in a popover; saving offers an Undo. Storage is the
 * caller's: `onSave(null)` removes the note.
 */
export interface NotePopoverCellProps {
  note: string | null;
  onSave: (note: string | null) => Promise<void>;
}

const stopRowClick = (event: MouseEvent): void => event.stopPropagation();

export function NotePopoverCell({ note, onSave }: NotePopoverCellProps): ReactElement {
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (): Promise<void> => {
    const next = draft.trim() || null;
    if (next === note) {
      setOpened(false);
      return;
    }
    const previous = note;
    setSaving(true);
    try {
      await onSave(next);
      setOpened(false);
      showUndoToast(next ? 'Note saved' : 'Note removed', () => {
        onSave(previous).catch((error: unknown) => alert.error(`Couldn't undo: ${errorMessage(error)}`));
      });
    } catch (error) {
      alert.error(`Couldn't save note: ${errorMessage(error)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    // duration 0, as in ListRangeControl: under jsdom the default transition never commits the dropdown.
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={280}
      withinPortal
      shadow="md"
      transitionProps={{ duration: 0 }}
    >
      <Popover.Target>
        <UnstyledButton
          aria-label={note ? 'Edit note' : 'Add a note'}
          title={note || 'Add a note'}
          onClick={(event) => {
            stopRowClick(event);
            setDraft(note ?? '');
            setOpened((open) => !open);
          }}
          style={{ display: 'inline-flex', padding: 4, borderRadius: 6 }}
        >
          <IconNote size={16} stroke={1.75} color={note ? 'black' : ORBIT_DISABLED} />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown onClick={stopRowClick}>
        <Text fz={13} fw={600} mb={8}>
          Note
        </Text>
        <Textarea
          aria-label="Note"
          autosize
          minRows={3}
          maxRows={8}
          maxLength={2000}
          value={draft}
          onChange={(event) => setDraft(event.currentTarget.value)}
          data-autofocus
        />
        <Group justify="flex-end" gap={8} mt={10}>
          <Button size="compact-sm" variant="default" onClick={() => setOpened(false)}>
            Cancel
          </Button>
          <Button size="compact-sm" color="dark" loading={saving} onClick={() => void save()}>
            Save
          </Button>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'unknown error';
}
